// https://codeburst.io/angular-2-simple-infinite-scroller-directive-with-rxjs-observables-a989b12d4fb1
import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, pairwise, takeUntil } from 'rxjs/operators';

interface ScrollPosition {
  sH: number;
  sT: number;
  cH: number;
}

const DEFAULT_SCROLL_POSITION: ScrollPosition = {
  sH: 0,
  sT: 0,
  cH: 0
};

@Directive({
    selector: '[appInfiniteScroller]',
    standalone: true
})
export class InfiniteScrollerDirective implements AfterViewInit, OnDestroy {
  @Input() immediateCallback: boolean;

  @Input() scrollPercent = 70;

  @Output() scrolled = new EventEmitter();

  private userScrolledDown$: Observable<[ScrollPosition, ScrollPosition]>;

  private requestOnScroll$;

  private destroyed$ = new Subject<void>();

  private scrollEvent$;

  constructor(private elm: ElementRef) {}

  ngAfterViewInit() {
    this.userScrolledDown$ = fromEvent(this.elm.nativeElement, 'scroll').pipe(
      takeUntil(this.destroyed$),
      map(
        (e: any): ScrollPosition => ({
          sH: e.target.scrollHeight,
          sT: e.target.scrollTop,
          cH: e.target.clientHeight
        })
      ),
      pairwise(),
      filter(positions => this.isUserScrollingDown(positions) && this.isScrollExpectedPercent(positions[1]))
    );

    this.requestCallbackOnScroll();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private requestCallbackOnScroll() {
    this.requestOnScroll$ = this.userScrolledDown$;

    if (this.immediateCallback) {
      this.requestOnScroll$ = this.requestOnScroll$.startWith([DEFAULT_SCROLL_POSITION, DEFAULT_SCROLL_POSITION]);
    }

    this.requestOnScroll$
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(1000),
        map(() => this.scrolled.emit())
      )
      .subscribe();
  }

  private isUserScrollingDown = (positions: [ScrollPosition, ScrollPosition]) => positions[0].sT < positions[1].sT;

  private isScrollExpectedPercent = (position: ScrollPosition) => (position.sT + position.cH) / position.sH > this.scrollPercent / 100;
}
